import { ActionsGroup } from '@elotech/components';
import { PedidoActionButton } from 'itbi-common/components';
import React from 'react';

const onPrintBoletoDefault = urlBoleto => window.open(urlBoleto, '_blank');

const renderEditButton = (requerimento, { onEdit }, props) => (
  <PedidoActionButton
    key="edit-button"
    data-test-id={`edit-button-${requerimento.id}`}
    icon="pencil-alt"
    title="Editar"
    onClick={() => onEdit(requerimento)}
    {...props}
  />
);

const renderEditIndeferidoButton = (
  requerimento,
  actions,
  { permiteEdicaoDeclaracaoIndeferida = false, ...props }
) =>
  permiteEdicaoDeclaracaoIndeferida
    ? renderEditButton(requerimento, actions, props)
    : null;

const renderViewButton = (requerimento, { onView }, props) =>
  props.fab ? null : (
    <PedidoActionButton
      key="view-button"
      data-test-id={`view-button-${requerimento.id}`}
      icon="eye"
      title="Visualizar"
      onClick={() => onView(requerimento)}
      {...props}
    />
  );

const renderAcceptButton = (requerimento, { onAccept }, props) =>
  requerimento.contestacao ? (
    <PedidoActionButton
      key="aceitar-button"
      data-test-id={`aceitar-button-${requerimento.id}`}
      icon="check-circle"
      title="Aceitar"
      onClick={() => onAccept(requerimento)}
      {...props}
    />
  ) : (
    undefined
  );

const renderContestButton = (requerimento, { onContest }, props) =>
  requerimento.contestacao ? (
    <PedidoActionButton
      key="contestar-button"
      data-test-id={`contestar-button-${requerimento.id}`}
      icon="exclamation-circle"
      title="Contestar"
      onClick={() => onContest(requerimento)}
      {...props}
    />
  ) : (
    undefined
  );

const renderRetificarButton = (
  requerimento,
  { retificarGuiaDeclacracao },
  { utilizaFluxoAutonomiaCidadao = false, ...props }
) => {
  if (utilizaFluxoAutonomiaCidadao && !requerimento.retificadora) {
    return (
      <PedidoActionButton
        key="retificar-button"
        data-test-id={`retificar-button-${requerimento.id}`}
        icon="edit"
        title="Auto-Regular"
        onClick={() => retificarGuiaDeclacracao(requerimento, 'CIDADAO')}
        {...props}
      />
    );
  }

  return null;
};

const renderPrintBoletoButton = (
  requerimento,
  { onPrintBoleto = onPrintBoletoDefault },
  props
) => {
  if (!requerimento.urlBoleto) {
    return null;
  }
  return (
    <PedidoActionButton
      key="imprimir-boleto-button"
      data-test-id={`print-boleto-button-${requerimento.id}`}
      icon="barcode"
      title="Imprimir Boleto"
      onClick={() => onPrintBoleto(requerimento.urlBoleto)}
      {...props}
    />
  );
};

const renderPrintCertidaoQuitacaoButton = (
  requerimento,
  { onDownloadQuitacao },
  { certidaoSeTotalmenteIsento, ...props }
) => {
  const {
    tipoItbi: { isencao: isento },
    valorIsencao,
    valorTransacaoItbi
  } = requerimento;
  const hasIsencao =
    isento && valorIsencao > 0 && valorIsencao !== valorTransacaoItbi;
  const canRenderButton = !isento || (hasIsencao && certidaoSeTotalmenteIsento);
  const renderButton = (
    <PedidoActionButton
      key="print-quitacao-button"
      data-test-id={`print-quitacao-button-${requerimento.id}`}
      icon="file-download"
      title="Imprimir Certidão de Quitação"
      onClick={() => onDownloadQuitacao(requerimento)}
      {...props}
    />
  );

  return canRenderButton && renderButton;
};

const renderGenerateDebitButton = (pedido, { onGenerateDebito }, props) =>
  !pedido.idDebito ? (
    <PedidoActionButton
      data-test-id="generate-debit-button"
      key="generate-debit-button"
      icon="dollar-sign"
      title="Gerar débito"
      onClick={() => onGenerateDebito(pedido)}
      {...props}
    />
  ) : null;

const renderPrintCertidaoIsencaoButton = (
  requerimento,
  { onPrintCertidaoIsencao },
  { certidaoSeTotalmenteIsento, ...props }
) => {
  const {
    tipoItbi: { isencao: isento, financiado },
    valorIsencao,
    valorTransacaoItbi,
    valorFinanciadoItbi
  } = requerimento;

  const isFinanciadoIsento =
    financiado && valorTransacaoItbi + valorFinanciadoItbi === 0;
  const isTotalmenteIsento =
    isento && valorIsencao > 0 && valorIsencao === valorTransacaoItbi;
  const isParcialmenteIsento =
    isento && valorIsencao > 0 && valorIsencao !== valorTransacaoItbi;
  const canRenderButton =
    isFinanciadoIsento ||
    isTotalmenteIsento ||
    (isParcialmenteIsento && !certidaoSeTotalmenteIsento);
  const renderButton = (
    <PedidoActionButton
      key="print-isencao-button"
      data-test-id={`print-isencao-button-${requerimento}`}
      icon="file-alt"
      title="Imprimir Certidão de Isenção/Imunidade"
      onClick={() => onPrintCertidaoIsencao(requerimento)}
      {...props}
    />
  );

  return canRenderButton && renderButton;
};

const renderPrintButton = (requerimento, actions, props) =>
  props.fab ? (
    <PedidoActionButton
      key="imprimir-button"
      data-test-id={`print-button-${requerimento.id}`}
      icon="print"
      title="Imprimir"
      onClick={() => window.print()}
      {...props}
    />
  ) : null;

export const renderCopyButton = (requerimento, { onCopy }, props) => (
  <PedidoActionButton
    key="copy-button"
    data-test-id={`copy-button-${requerimento.id}`}
    icon="copy"
    title="Copiar Para Nova Declaração"
    onClick={() => onCopy(requerimento)}
    {...props}
  />
);

const renderGerarBoletoButton = (requerimento, { onGenerateBoleto }, props) => {
  if (!requerimento.boletoGerado && requerimento.idDebito) {
    return (
      <PedidoActionButton
        data-test-id="boleto-button"
        key="boleto-button"
        icon="barcode"
        title="Gerar Boleto"
        onClick={() => onGenerateBoleto(requerimento)}
        {...props}
      />
    );
  }

  return null;
};

const renderRemoveButton = (requerimento, { onRemove }, props) => (
  <PedidoActionButton
    key="trash-button"
    data-test-id={`delete-button-${requerimento.id}`}
    icon="trash-alt"
    title="Descartar rascunho"
    onClick={() => onRemove(requerimento)}
    {...props}
  />
);

const actionButtons = {
  RASCUNHO: [renderEditButton, renderRemoveButton],
  AGUARDANDO_ANALISE: [renderViewButton, renderPrintButton],
  ANALISANDO: [renderViewButton, renderPrintButton],
  INDEFERIDO: [
    renderEditIndeferidoButton,
    renderCopyButton,
    renderViewButton,
    renderPrintButton
  ],
  DEFERIDO: [renderViewButton, renderPrintButton],
  AGUARDANDO_NOTIFICACAO: [
    renderPrintBoletoButton,
    renderViewButton,
    renderPrintButton
  ],
  AGUARDANDO_GERACAO_BOLETO: [
    renderViewButton,
    renderGenerateDebitButton,
    renderPrintButton,
    renderGerarBoletoButton
  ],
  AGUARDANDO_PAGAMENTO: [
    renderRetificarButton,
    renderPrintBoletoButton,
    renderViewButton,
    renderPrintButton
  ],
  EM_RETIFICACAO: [
    renderPrintBoletoButton,
    renderViewButton,
    renderPrintButton
  ],
  PENDENTE_TRANSFERENCIA: [
    renderPrintCertidaoQuitacaoButton,
    renderViewButton,
    renderPrintButton,
    renderPrintCertidaoIsencaoButton
  ],
  AGUARDANDO_HOMOLOGACAO: [renderViewButton, renderPrintButton],
  TRANSFERENCIA_MANUAL: [
    renderPrintCertidaoQuitacaoButton,
    renderViewButton,
    renderPrintButton,
    renderPrintCertidaoIsencaoButton
  ],
  TRANSFERIDO: [
    renderPrintCertidaoQuitacaoButton,
    renderViewButton,
    renderPrintButton,
    renderPrintCertidaoIsencaoButton
  ],
  DEFERIDO: [
    renderPrintCertidaoQuitacaoButton,
    renderViewButton,
    renderPrintButton,
    renderPrintCertidaoIsencaoButton
  ],
  AGUARDANDO_ACEITE: [
    renderViewButton,
    renderAcceptButton,
    renderContestButton
  ],
  EM_CONTESTACAO: [renderViewButton],
  CANCELADO: [renderViewButton, renderPrintButton]
};

export const renderActionButtons = (requerimento, actions, options = {}) => {
  return (
    <ActionsGroup>
      {(actionButtons[requerimento.situacaoITBI.name] || [])
        .filter(renderFunction => renderFunction !== renderGenerateDebitButton)
        .map(renderFunction => renderFunction(requerimento, actions, options))
        .filter(item => !!item)}
    </ActionsGroup>
  );
};

export const renderFabButtons = (requerimento, actions, options = {}) => {
  return (actionButtons[requerimento.situacaoItbi.name] || [])
    .map(renderFunction =>
      renderFunction(requerimento, actions, { ...options, fab: true })
    )
    .filter(item => !!item);
};
